import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Empty } from "./Common";
import { TemplatePicker } from "./TemplatePicker";

export function MessageThread({ url, self, testId = "thread", placeholder = "Write a message…", templateVars }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const load = useCallback(() => api.get(url).then((r) => setItems(r.data)).catch(() => {}), [url]);
  useEffect(() => { load(); const h = setInterval(load, 15000); return () => clearInterval(h); }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [items.length]);
  const send = async () => {
    setBusy(true);
    try { await api.post(url, { text }); setText(""); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-[var(--radius)] border bg-card flex flex-col" data-testid={testId}>
      <div className="max-h-[420px] min-h-[160px] overflow-y-auto p-4 space-y-2">
        {!items.length && <p className="text-sm text-muted-foreground text-center py-8" data-testid={`${testId}-empty`}>No messages yet</p>}
        {items.map((m) => (
          <div key={m.id} className={`flex ${m.sender === self ? "justify-end" : "justify-start"}`} data-testid={`${testId}-msg-${m.id}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === self ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
              <div className="whitespace-pre-wrap">{m.text}</div>
              <div className="text-[10px] opacity-70 mt-1">{m.author_name} · {new Date(m.created_at).toLocaleString()}</div>
            </div>
          </div>))}
        <div ref={end} />
      </div>
      {templateVars && <div className="border-t px-3 pt-3"><TemplatePicker vars={templateVars} testId={`${testId}-templates`} onPick={(t) => setText(t.body)} /></div>}
      <div className={`p-3 flex gap-2 items-end ${templateVars ? "" : "border-t"}`}>
        <Textarea rows={2} className="flex-1" data-testid={`${testId}-input`} placeholder={placeholder} value={text} onChange={(e) => setText(e.target.value)} />
        <Button className="rounded-full" data-testid={`${testId}-send`} disabled={busy || !text.trim()} onClick={send}><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export function PatientThreads() {
  const [items, setItems] = useState(null);
  useEffect(() => { const load = () => api.get("/threads").then((r) => setItems(r.data)); load(); const h = setInterval(load, 15000); return () => clearInterval(h); }, []);
  if (!items) return null;
  return (
    <div className="space-y-2" data-testid="patient-threads">
      {!items.length && <Empty text="No patient messages yet. Open a patient profile and click Message to start." testId="patient-threads-empty" />}
      {items.map((t) => (
        <Link key={t.client_id} to={`/app/clients/${t.client_id}?tab=messages`} data-testid={`patient-thread-${t.client_id}`} className="flex items-center gap-3 rounded-[var(--radius)] border bg-card p-3 hover:bg-muted/40">
          <div className="flex-1 min-w-0"><div className="font-medium text-sm">{t.client_name}</div><div className="text-xs text-muted-foreground truncate">{t.last_sender === "clinic" ? "You: " : ""}{t.last}</div></div>
          {t.unread > 0 && <span className="text-[10px] rounded-full bg-primary text-primary-foreground px-2 py-0.5" data-testid={`patient-thread-unread-${t.client_id}`}>{t.unread} new</span>}
          <span className="text-[10px] text-muted-foreground">{new Date(t.updated_at).toLocaleDateString()}</span>
        </Link>))}
    </div>
  );
}
